/**
 * skin_shading — the SKIN SIGNAL for the near versona actors (shader gaps program round 2, agent D, 2026-09-29).
 * PURE (no three): the harness `verify:skin` and the served-texture sampler `verify:skin:served` run it in node; the
 * material (`skin_material.ts`) mirrors every formula here in TSL.
 *
 * THE BLOCKER (the owner's report §4.4): every versona rig is ONE Meshy material with ONE atlas — skin, hair, eyes,
 * clothes, leather and metal in the same map, cut into hundreds of tiny UV islands — and no mask says which texels are
 * skin. So the mask is derived PER FRAGMENT from the base colour itself:
 *
 *   1. CHROMA (YCbCr, BT.601 full range on the sRGB-encoded albedo — the space the skin-detection literature uses:
 *      Chai & Ngan 1999's window Cb 77–127 / Cr 133–173, Kolkur et al. 2017 arXiv 1708.02694). Measured on the 18
 *      SERVED rigs (`verify:skin:served`), that window alone flags 95–100 % of the tan robes (aurelian), the rust
 *      jumpsuits (colonists, child) and every brown boot — it is a PHOTO classifier, and skin, leather, tan cloth and
 *      brown hair share a hue. The chroma is therefore split into its ANGLE φ = atan2(Cr', −Cb') (yellow ↔ red) and
 *      its RELATIVE size C/Y (≈ saturation): skin sits at φ 38–62° with C/Y 0.08–0.35 over the whole Fitzpatrick
 *      range (physicallybased.info Skin I–VI, the Monk scale); rust cloth is C/Y 0.40–0.58, tan robes φ ≈ 34° at a
 *      LIGHT luminance — so the angle's lower edge rises with luminance (dark skin is yellower than light skin).
 *   2. LUMINANCE guards: near-black (hair, shadows baked into the albedo) and near-white texels are not skin; a
 *      per-BODY floor (the table below, measured from each body's own hand texels) removes hair and leather that are
 *      darker than that body's skin.
 *   3. METAL: metalness ≥ 0.5 is never skin (the MR map).
 *   4. BONES: texels skinned to the FEET (LeftFoot / LeftToeBase / Right…) are excluded — every served body wears
 *      boots, and brown leather is skin-coloured.
 *
 * THE RESPONSE (per light, the lighting model — skin_material.ts), after Penner 2011 (pre-integrated skin: a
 * (N·L, curvature) LUT, per-channel bent normals) and NVIDIA FaceWorks (the LUT stores the scatter DELTA; the green
 * and blue normals lerp between the shading and the blurred normal; deep scatter = transmittance × sat(0.3 − N·L)):
 *
 *   D_rgb   = sat(Δ_LUT(N_b·L, κ) + (sat(N_b·L), sat(N_g·L), sat(N_b'·L)))       (FaceWorks EvaluateSSSDirectLight)
 *   a_rgb   = mask · T_rgb(thickness(κ))                                          (Jimenez 2010's transmittance)
 *   out_rgb = mix(sat(N·L), D_rgb, mask) · (1 − a) + a · sat(0.3 − N_b·L) / 1.6
 *
 * The 1/1.6 makes the back lobe integrate like Lambert (2π∫sat(0.3 − μ)dμ = 1.6π), and every LUT row is
 * renormalised so ∫D dω = π — the WHITE FURNACE holds exactly per channel (`skinFurnaceCpu`). Specular is untouched.
 */

// ── The classifier ─────────────────────────────────────────────────────────────────────────────────────────────────

export interface SkinClassifier {
  /** the chroma angle's lower edge (degrees) for DARK albedo … */
  phiLoDark: number;
  /** … and for LIGHT albedo (tan cloth is yellower than light skin), blended over yLight0..yLight1 */
  phiLoLight: number;
  yLight0: number; yLight1: number;
  /** soft half-width of the lower angle edge */
  phiEdge: number;
  /** the upper angle edge (toward magenta / pure red) and its half-width */
  phiHi: number; phiHiEdge: number;
  /** relative chroma C/Y: greys below relLo0..relLo1, saturated cloth above relHi0..relHi1 */
  relLo0: number; relLo1: number; relHi0: number; relHi1: number;
  /** the default luminance floor (a body may raise it) and the white ceiling */
  yLo0: number; yLo1: number; yHi0: number; yHi1: number;
  /** metalness fade */
  metal0: number; metal1: number;
}

export const SKIN_CLASSIFIER: Readonly<SkinClassifier> = Object.freeze({
  phiLoDark: 32, phiLoLight: 36, yLight0: 0.35, yLight1: 0.65, phiEdge: 6,
  phiHi: 76, phiHiEdge: 6,
  relLo0: 0.05, relLo1: 0.09, relHi0: 0.32, relHi1: 0.42,
  yLo0: 0.10, yLo1: 0.16, yHi0: 0.93, yHi1: 0.985,
  metal0: 0.3, metal1: 0.5,
});

/** A body's override (the table below): a raised luminance floor, or no skin at all. */
export interface SkinBodyParams {
  /** the luminance floor's soft edge [y0, y1] (sRGB luma 0..1), raised above the body's hair / leather */
  yFloor?: readonly [number, number];
  /** this body shows no human skin (a sealed suit) — the material is left exactly as it was */
  off?: boolean;
  /** the regions that may be skin on this body (default head / arms / hands — `DEFAULT_SKIN_REGIONS`) */
  regions?: readonly SkinRegion[];
  /** why (measured — `verify:skin:served`) */
  note?: string;
}

export const smoothstep = (a: number, b: number, x: number): number => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

/** The sRGB OETF (linear → encoded), the exact piecewise curve (the classifier's thresholds are in encoded space). */
export function srgbEncode(c: number): number {
  const x = Math.min(1, Math.max(0, c));
  return x <= 0.0031308 ? 12.92 * x : 1.055 * Math.pow(x, 1 / 2.4) - 0.055;
}
export function srgbDecode(c: number): number {
  const x = Math.min(1, Math.max(0, c));
  return x <= 0.04045 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
}

/** BT.601 full-range luma and centred chroma of an sRGB-ENCODED colour (0..1). */
export function ycc(r: number, g: number, b: number): { y: number; cb: number; cr: number } {
  return {
    y: 0.299 * r + 0.587 * g + 0.114 * b,
    cb: -0.168736 * r - 0.331264 * g + 0.5 * b,
    cr: 0.5 * r - 0.418688 * g - 0.081312 * b,
  };
}

/** The classifier's intermediate features (the lever and the harness print them). */
export function skinFeatures(r: number, g: number, b: number): { y: number; cb: number; cr: number; c: number; rel: number; phi: number } {
  const { y, cb, cr } = ycc(r, g, b);
  const c = Math.hypot(cb, cr);
  return { y, cb, cr, c, rel: c / Math.max(y, 0.02), phi: (Math.atan2(cr, -cb) * 180) / Math.PI };
}

/**
 * The soft skin weight (0..1) of an sRGB-ENCODED albedo (0..1) with its metalness. The TSL twin is
 * `skinMaskNode` (skin_material.ts) — same order of operations.
 */
export function skinWeightSrgb(r: number, g: number, b: number, metal = 0, p: SkinClassifier = SKIN_CLASSIFIER, body: SkinBodyParams | null = null): number {
  if (body?.off) return 0;
  const f = skinFeatures(r, g, b);
  const lo = p.phiLoDark + (p.phiLoLight - p.phiLoDark) * smoothstep(p.yLight0, p.yLight1, f.y);
  const wPhi = smoothstep(lo - p.phiEdge, lo + p.phiEdge, f.phi) * (1 - smoothstep(p.phiHi - p.phiHiEdge, p.phiHi + p.phiHiEdge, f.phi));
  const wRel = smoothstep(p.relLo0, p.relLo1, f.rel) * (1 - smoothstep(p.relHi0, p.relHi1, f.rel));
  const floor = body?.yFloor ?? [p.yLo0, p.yLo1];
  const wY = smoothstep(floor[0], floor[1], f.y) * (1 - smoothstep(p.yHi0, p.yHi1, f.y));
  const wM = 1 - smoothstep(p.metal0, p.metal1, metal);
  return wPhi * wRel * wY * wM;
}

/** The same weight from a LINEAR albedo (what the shader's diffuseColor holds). */
export function skinWeightLinear(r: number, g: number, b: number, metal = 0, p: SkinClassifier = SKIN_CLASSIFIER, body: SkinBodyParams | null = null): number {
  return skinWeightSrgb(srgbEncode(r), srgbEncode(g), srgbEncode(b), metal, p, body);
}

/** The textbook window (Chai & Ngan 1999) — kept as the harness's baseline: why it is not used. */
export function chaiNganSkin(r: number, g: number, b: number): boolean {
  const { cb, cr } = ycc(r, g, b);
  const Cb = 128 + 255 * cb, Cr = 128 + 255 * cr;
  return Cb >= 77 && Cb <= 127 && Cr >= 133 && Cr <= 173;
}

// ── Bones ──────────────────────────────────────────────────────────────────────────────────────────────────────────

/**
 * The body regions a skin mask may cover, by the bone a vertex is skinned to. THE REGION PRIOR: on the 38 served
 * bodies (18 versonas + 20 player presets, `verify:skin:served`) the bare skin is the head and neck, the hands and —
 * in rolled sleeves, a T-shirt, a tank top — the arms; no served body shows a bare torso or bare legs, and every one
 * wears boots. Where the chroma alone was measured, the torso and legs carried 17–86 % of "skin" on six player bodies
 * (brown leather, tan armour, olive cloth the colour of their own skin) and the feet 27–94 % (boots). So by default
 * only the head / arms / hands may be skin; a body in the table may narrow that (gloves, a sealed sleeve) or widen it.
 */
export type SkinRegion = 'head' | 'arms' | 'hands' | 'torso' | 'legs' | 'feet';
export const SKIN_REGIONS: readonly SkinRegion[] = ['head', 'arms', 'hands', 'torso', 'legs', 'feet'];
export const DEFAULT_SKIN_REGIONS: readonly SkinRegion[] = ['head', 'arms', 'hands'];

/** The region a bone belongs to, by name (Meshy / Mixamo names: Head, head_end, neck, LeftForeArm, LeftUpLeg, …). */
export function regionOfBone(name: string): SkinRegion {
  if (/head|neck/i.test(name)) return 'head';
  if (/hand|finger|thumb/i.test(name)) return 'hands';
  if (/foot|toe/i.test(name)) return 'feet';
  if (/arm|shoulder|clavicle/i.test(name)) return 'arms';
  if (/leg|thigh|knee|calf|shin/i.test(name)) return 'legs';
  return 'torso';
}

/**
 * The per-material bone mask: bit j set when skeleton bone j lies in an allowed region. 24 bits at most (the mask
 * travels as a float uniform — exact below 2^24); a bone past index 23 is never skin.
 */
export const SKIN_MAX_BONES = 24;
export function skinBoneMask(boneNames: readonly string[], regions: readonly SkinRegion[] = DEFAULT_SKIN_REGIONS): number {
  let m = 0;
  for (let j = 0; j < Math.min(boneNames.length, SKIN_MAX_BONES); j++) if (regions.includes(regionOfBone(boneNames[j]))) m += 2 ** j;
  return m;
}
/** Whether bone j is allowed by a mask (the shader's `(uint(mask) >> j) & 1u`). */
export function maskAllows(mask: number, j: number): boolean {
  return j >= 0 && j < SKIN_MAX_BONES && Math.floor(mask / 2 ** j) % 2 === 1;
}
/** A vertex's allowed weight: Σ w_k over its joints in the mask (the shader's varying), and the soft gate over it. */
export function allowedWeight(joints: ArrayLike<number>, weights: ArrayLike<number>, mask: number): number {
  let s = 0, t = 0;
  for (let k = 0; k < 4; k++) { t += weights[k]; if (maskAllows(mask, joints[k])) s += weights[k]; }
  return t > 0 ? s / t : 0;
}
export const SKIN_ALLOW_EDGE: readonly [number, number] = [0.35, 0.65];
export function allowGate(allowed: number): number { return smoothstep(SKIN_ALLOW_EDGE[0], SKIN_ALLOW_EDGE[1], allowed); }

// ── The per-body table (measured on the SERVED rigs — `npm run verify:skin:served` prints and checks it) ─────────────

/**
 * Keyed by the body key WITHOUT the `avatar:` / `avatar-rig:` prefix. A PLAYER rig is keyed by its CONTENT
 * (lib/player_clips.ts playerSkinKey): `player5_<name>` for a rig that carries the wave-5 clips, under whatever key
 * it is registered or worn; `player_<name>` for the R8 rigs. `yFloor` sits just under the body's own hand
 * skin (its 5th percentile of luma, minus a margin), so the hair, belts, satchels and boot shafts that are DARKER than
 * that body's skin drop out; a body missing here uses the default floor (dark skin stays skin).
 */
const HH: readonly SkinRegion[] = ['head', 'hands'];
const H0: readonly SkinRegion[] = ['head'];
export const SKIN_BODY_TABLE: Readonly<Record<string, SkinBodyParams>> = Object.freeze({
  // ── the versona roster (wave 2 hi-res rigs) ──
  alien_humanoid_a: { yFloor: [0.37, 0.43], note: 'hands 92 % skin, luma p5 0.47 — the dark bob hair drops out' },
  alien_humanoid_b: { yFloor: [0.31, 0.37], note: 'hands 84 %, p5 0.41 — the brown boot shafts drop out' },
  child_colonist: { yFloor: [0.36, 0.42], note: 'hands 94 %, p5 0.46 — the brown curls and the satchel drop out' },
  civ_aurelian_citizen: { yFloor: [0.25, 0.31], regions: HH, note: 'hands 91 %, p5 0.35 — the tan robe\'s sleeves share skin\'s chroma (43 % of the arms): head and hands' },
  colonist_jumpsuit_a: { yFloor: [0.25, 0.31], regions: HH, note: 'long rust sleeves read as skin at 28 % — head and hands only' },
  colonist_jumpsuit_b: { yFloor: [0.37, 0.43], regions: HH, note: 'long rust sleeves read as skin at 27 % — head and hands only' },
  crew_captain: { yFloor: [0.39, 0.45], note: 'a T-shirt: the arms are bare' },
  crew_cook: { yFloor: [0.27, 0.33], note: 'rolled sleeves: the forearms are bare' },
  crew_deckhand: { yFloor: [0.42, 0.48], note: 'a tank top: the arms are bare' },
  crew_engineer: { regions: H0, note: 'brown work gloves (hand luma p5 0.16) — the head only' },
  crew_medic: { yFloor: [0.44, 0.5], note: 'hands 93 %, p5 0.54' },
  crew_quartermaster: { yFloor: [0.45, 0.51], note: 'hands 79 %, p5 0.55 — the leather vest is torso' },
  crew_steward: { yFloor: [0.56, 0.62], note: 'hands 93 %, p5 0.66' },
  env_suit_worker: { off: true, note: 'a sealed suit and a helmet — the visor is not a face' },
  merchant_robed: { yFloor: [0.42, 0.48], note: 'hands 84 %, p5 0.52' },
  official_uniform: { yFloor: [0.45, 0.51], note: 'hands 93 %, p5 0.55' },
  // ── versona wave 4 (2026-10-08; measured on the served rigs — `SKIN_SERVED_ONLY=… npm run verify:skin:served`) ──
  civ_aurelian_artisan_f: { regions: H0, note: 'brown leather work gloves (hand luma p5 0.17) and gold jacket sleeves at 50 % — the head only' },
  civ_aurelian_elder_f: { yFloor: [0.35, 0.41], regions: HH, note: 'hands 98 %, p5 0.45; the tan tunic sleeves read as skin at 74 % — head and hands' },
  civ_aurelian_elder_m: { yFloor: [0.19, 0.25], regions: HH, note: 'hands 61 %, p5 0.29; the sand coat sleeves at 33 % — head and hands' },
  civ_aurelian_fieldhand_m: { yFloor: [0.34, 0.4], note: 'a sleeveless undersuit: the arms are bare above the wrapped forearms (65 %)' },
  civ_aurelian_scholar_f: { yFloor: [0.35, 0.41], regions: HH, note: 'hands 85 %, p5 0.45; white sleeves — head and hands' },
  civ_aurelian_weaver_f: { yFloor: [0.27, 0.33], regions: HH, note: 'hands 94 %, p5 0.37; the wrapped sleeves at 28 % — head and hands' },
  civ_aurelian_youth_m: { yFloor: [0.25, 0.31], regions: HH, note: 'hands 98 %, p5 0.35; the sand jacket reads as skin at 90 % of the arms — head and hands' },
  civ_borean_artisan_f: { yFloor: [0.38, 0.44], regions: HH, note: 'hands 71 %, p5 0.48; thermal sleeves — head and hands' },
  civ_borean_elder_f: { yFloor: [0.28, 0.34], regions: HH, note: 'hands 87 %, p5 0.38; the coat sleeves at 21 % — head and hands' },
  civ_borean_elder_m: { yFloor: [0.46, 0.52], regions: HH, note: 'hands 70 %, p5 0.56; a long beard and a fur collar on the head bone (10 %)' },
  civ_borean_miner_m: { yFloor: [0.37, 0.43], regions: HH, note: 'hands 66 %, p5 0.47; a padded suit — head and hands' },
  civ_borean_parka_f: { regions: HH, note: 'hands 53 % (the coat cuffs reach the knuckles): no raised floor; head and hands' },
  civ_borean_ranger_m: { regions: H0, note: 'grey gloves (hands 0 %) under a raised hood — the head only' },
  civ_borean_youth_f: { yFloor: [0.53, 0.59], regions: HH, note: 'hands 92 %, p5 0.63; the visor and the ponytail are most of the head bone (9 %)' },
  colonist_drifter_m: { yFloor: [0.2, 0.26], regions: HH, note: 'hands 88 %, p5 0.30; the patched coat at 37 % of the arms — head and hands' },
  colonist_pilgrim_f: { yFloor: [0.17, 0.23], regions: HH, note: 'hands 97 %, p5 0.27; the earth-brown coat reads as skin at 88 % of the arms — head and hands' },
  guard_militia_f: { regions: HH, note: 'fingerless gloves (hands 34 %): no raised floor; head and hands' },
  guard_militia_m: { yFloor: [0.42, 0.48], regions: HH, note: 'hands 79 %, p5 0.52; plate and a fitted suit — head and hands' },
  trainer_instructor_m: { yFloor: [0.46, 0.52], regions: HH, note: 'hands 64 %, p5 0.56; a long uniform coat — head and hands' },
  vendor_trader_f: { yFloor: [0.31, 0.37], regions: HH, note: 'hands 79 %, p5 0.41; the violet coat\'s brass trim at 23 % of the arms — head and hands' },
  // ── the twenty player presets (the scanned bodies) ──
  player_archivist: { yFloor: [0.14, 0.2], regions: HH, note: 'hands 85 %, p5 0.24; robe sleeves on the arms — head and hands' },
  player_circuitwright: { yFloor: [0.17, 0.23], note: 'hands 67 %, p5 0.27' },
  player_duskranger: { yFloor: [0.13, 0.19], regions: HH, note: 'the olive cloak over the arms reads as skin at 33 % — head and hands' },
  player_emberchef: { yFloor: [0.26, 0.32], note: 'rolled sleeves, tattooed forearms: the arms are bare' },
  player_forgemaster: { yFloor: [0.11, 0.17], note: 'bare tattooed arms; the leather apron and trousers are torso / legs' },
  player_gearhart: { yFloor: [0.16, 0.22], note: 'hands 62 %, p5 0.26' },
  player_medica: { yFloor: [0.2, 0.26], regions: HH, note: 'red sleeve trims at 33 % — head and hands' },
  player_pathfinder: { regions: H0, note: 'armoured gloves with claws — the head only' },
  player_quarrier: { regions: H0, note: 'armoured gloves — the head only' },
  player_starling: { yFloor: [0.53, 0.59], note: 'hands 85 %, p5 0.63' },
  player_stellarch: { yFloor: [0.51, 0.57], note: 'hands 75 %, p5 0.61' },
  player_stonewright: { yFloor: [0.32, 0.38], note: 'hands 86 %, p5 0.42' },
  player_tidewalker: { yFloor: [0.18, 0.24], note: 'hands 74 %, p5 0.28' },
  player_verdant: { yFloor: [0.37, 0.43], note: 'hands 62 %, p5 0.47' },
  player_voltkeeper: { yFloor: [0.13, 0.19], note: 'hands 71 %, p5 0.23' },
  // ── the twenty player presets, WAVE 5 (2026-10-08; measured on the served rigs — `SKIN_SERVED_ONLY=player5_… npm run verify:skin:served`) ──
  player5_archivist: { yFloor: [0.13, 0.19], regions: HH, note: 'hands 94 %, p5 0.23; the charcoal robe\'s wide gold-banded sleeves read as skin at 54 % of the arms — head and hands' },
  player5_circuitwright: { yFloor: [0.25, 0.31], note: 'hands 95 %, p5 0.35; rolled sleeves: the forearms are bare (8 %)' },
  player5_duskranger: { yFloor: [0.1, 0.16], regions: HH, note: 'hands 89 %, p5 0.17; the moss poncho and the bracers read as skin at 52 % of the arms — head and hands' },
  player5_emberchef: { yFloor: [0.29, 0.35], note: 'hands 96 %, p5 0.39; rolled sleeves, tattooed forearms: the arms are bare' },
  player5_forgemaster: { note: 'a brass gauntlet on one hand (hands 52 %): no raised floor; sleeveless — the bare arms stay skin (73 %)' },
  player5_frostrunner: { regions: HH, note: 'a pale face and fingertips the classifier barely reads (head 4 %, hands 10 %): no raised floor; head and hands' },
  player5_gearhart: { yFloor: [0.21, 0.27], note: 'hands 94 %, p5 0.31; the coverall is tied at the waist: the arms are bare' },
  player5_lumenweaver: { regions: HH, note: 'pale hands under the veil panels (42 %), a face the classifier does not read (2 %): no raised floor; head and hands' },
  player5_medica: { yFloor: [0.12, 0.18], regions: HH, note: 'fingerless gloves (hands 66 %), p5 0.22; the cream sleeves read as skin at 51 % — head and hands' },
  player5_nightcourier: { regions: H0, note: 'black gloves; nothing reads as skin (head 0 %, hands 0 %) — the head only' },
  player5_pathfinder: { regions: HH, note: 'the bronze arm plates read as skin at 68 %; hands 45 %: no raised floor; head and hands' },
  player5_quarrier: { regions: H0, note: 'heavy gloves (hands 25 %) and a padded suit — the head only' },
  player5_skysong: { yFloor: [0.24, 0.30], note: 'between its two rigs: full rig hands 90 %, p5 0.32 → [0.22, 0.28]; light rig (the thinnest atlas of the twenty) p5 0.37 → [0.27, 0.33]; a cropped top: the arms are bare under the sheer scarves (70 %)' },
  player5_skywarden: { regions: H0, note: 'red gloves and a hard-shell suit (hands 9 %) — the head only' },
  player5_starling: { yFloor: [0.66, 0.72], note: 'hands 74 %, p5 0.76' },
  player5_stellarch: { yFloor: [0.62, 0.68], note: 'hands 68 %, p5 0.72' },
  player5_stonewright: { yFloor: [0.33, 0.39], note: 'hands 93 %, p5 0.43' },
  player5_tidewalker: { yFloor: [0.19, 0.25], note: 'hands 86 %, p5 0.29; bare feet are excluded like every foot' },
  player5_verdant: { yFloor: [0.35, 0.41], note: 'hands 62 %, p5 0.45' },
  player5_voltkeeper: { yFloor: [0.15, 0.21], regions: HH, note: 'hands 81 %, p5 0.25; the amber sleeve stripes read as skin at 18 % of the arms — head and hands' },
});

/** The override for a body key (`avatar:x`, `avatar-rig:x` or `x`), or null. */
export function skinBodyParams(bodyKey: string): SkinBodyParams | null {
  const k = bodyKey.replace(/^avatar(-rig)?:/, '');
  return SKIN_BODY_TABLE[k] ?? null;
}

// ── The response ───────────────────────────────────────────────────────────────────────────────────────────────────

/** The curvature LUT (engine `bakeSkinCurvatureLut`): radii 1–100 mm, sqrt V, 64 × 64, 48 samples per Gaussian. */
export const SKIN_LUT = Object.freeze({ width: 64, height: 64, rMinMm: 1, rMaxMm: 100, steps: 48 });

/**
 * The back lobe (FaceWorks / Jimenez: sat(0.3 − N·L)) and its Lambert-matching normaliser. The lobe saturates at 1 for
 * μ ≤ w − 1, so 2∫_{−1}^{w} sat(w − μ) dμ = 2(w + ½) = 1 + 2w (1.6 at w = 0.3), against Lambert's 2∫_0^1 μ dμ = 1.
 * (1/1.69 — the unsaturated lobe's integral — left 5 % of the transmitted light unaccounted: the harness caught it.)
 */
export const SKIN_BACK_WRAP = 0.3;
export const SKIN_BACK_NORM = 1 / (1 + 2 * SKIN_BACK_WRAP);

/**
 * The transmission's thickness. The BAKE (`skin_curvature.ts`) measures one chord per vertex: a ray from the surface
 * into the body (−N) to the surface it meets, in mm (the shader converts from the geometry's units per pixel). A chord
 * is not the path back-lit light scatters along — light enters an ear near its rim and a Meshy ear is a solid lump
 * (15–17 mm chords measured on the served steward; a finger 15 mm) — so, like Jimenez et al. 2010's own
 * "translucency" factor on their shadow-map distance, the chord is SCALED before the profile: 0.1 turns a 17 mm ear
 * into 1.7 mm (≈ 14 % red transmitted, green ≈ 0.1 %, blue 0) and leaves a face (a chord past the 50 mm reach)
 * opaque. Tuned on the bed's back-lit ear (0.12 read as a faint tint); `__skin().setTransmission` scales the result.
 */
export const SKIN_TRANSLUCENCY_SCALE = 0.1;
export const SKIN_THICKNESS_MIN_MM = 0.4;
export const SKIN_THICKNESS_MAX_MM = 30;
/** The slab the profile sees for a baked chord (mm): scaled, clamped. */
export function skinThicknessMm(chordMm: number): number {
  return Math.min(SKIN_THICKNESS_MAX_MM, Math.max(SKIN_THICKNESS_MIN_MM, SKIN_TRANSLUCENCY_SCALE * chordMm));
}
/** A chord too long for any light to cross (the bake's cap — the default when a caller knows none). */
export const SKIN_CHORD_OPAQUE_MM = 1e3;

/** The six Gaussians (d'Eon / Jimenez) — the transmittance the shader evaluates analytically. */
export const SKIN_PROFILE: ReadonlyArray<{ v: number; w: readonly [number, number, number] }> = [
  { v: 0.0064, w: [0.233, 0.455, 0.649] },
  { v: 0.0484, w: [0.100, 0.336, 0.344] },
  { v: 0.187, w: [0.118, 0.198, 0.000] },
  { v: 0.567, w: [0.113, 0.007, 0.007] },
  { v: 1.99, w: [0.358, 0.004, 0.000] },
  { v: 7.41, w: [0.078, 0.000, 0.000] },
];
export function transmittanceRgb(dMm: number): [number, number, number] {
  const out: [number, number, number] = [0, 0, 0];
  for (const g of SKIN_PROFILE) { const e = Math.exp(-(dMm * dMm) / g.v); out[0] += g.w[0] * e; out[1] += g.w[1] * e; out[2] += g.w[2] * e; }
  return out;
}

/** Lever-tunable shading constants (the material reads them from uniforms; `__skin()` shows them). */
export interface SkinShadingParams {
  /** scales the measured curvature before the LUT (1 = physical units) */
  curvatureScale: number;
  /** the transmission gain (0 = none, 1 = the full Jimenez fraction) */
  transmission: number;
}
export const SKIN_DEFAULTS: Readonly<SkinShadingParams> = Object.freeze({ curvatureScale: 1, transmission: 1 });

/**
 * The LUT's delta at (u, v) the way the GPU's linear filter reads it (texel centres at (i + ½)/W, clamped at the edges)
 * — the bed compares the shader against the oracle with this.
 */
export function sampleLutBilinear(lut: { data: Float32Array; width: number; height: number }, u: number, v: number): [number, number, number] {
  const fx = Math.min(lut.width - 1, Math.max(0, u * lut.width - 0.5));
  const fy = Math.min(lut.height - 1, Math.max(0, v * lut.height - 0.5));
  const x0 = Math.floor(fx), y0 = Math.floor(fy), x1 = Math.min(lut.width - 1, x0 + 1), y1 = Math.min(lut.height - 1, y0 + 1);
  const tx = fx - x0, ty = fy - y0;
  const at = (x: number, y: number, c: number): number => lut.data[(y * lut.width + x) * 4 + c];
  const out: [number, number, number] = [0, 0, 0];
  for (let c = 0; c < 3; c++) {
    const a = at(x0, y0, c) * (1 - tx) + at(x1, y0, c) * tx;
    const b = at(x0, y1, c) * (1 - tx) + at(x1, y1, c) * tx;
    out[c] = a * (1 - ty) + b * ty;
  }
  return out;
}

/**
 * The CPU oracle of one light's diffuse factor (the multiplier on lightColor · albedo/π), for the harness — exactly the
 * shader's arithmetic with the LUT sampled at the texel centre nearest (u, v) (`bilinear: true` = the GPU's filter).
 */
export function skinDiffuseCpu(args: {
  nlShade: number; nlBlur: number; nlG: number; nlB: number;
  curvaturePerMm: number; mask: number; transmission?: number;
  /** the baked chord (mm); default opaque */
  chordMm?: number;
  lut: { data: Float32Array; width: number; height: number };
  lutV: (c: number) => number;
  bilinear?: boolean;
}): [number, number, number] {
  const { lut } = args;
  const u = args.nlBlur * 0.5 + 0.5, v = args.lutV(args.curvaturePerMm);
  const x = Math.min(lut.width - 1, Math.max(0, Math.round(u * lut.width - 0.5)));
  const y = Math.min(lut.height - 1, Math.max(0, Math.round(v * lut.height - 0.5)));
  const i = (y * lut.width + x) * 4;
  const dl = args.bilinear ? sampleLutBilinear(lut, u, v) : [lut.data[i], lut.data[i + 1], lut.data[i + 2]];
  const sat = (t: number): number => Math.min(1, Math.max(0, t));
  const nl = [sat(args.nlBlur), sat(args.nlG), sat(args.nlB)];
  const T = transmittanceRgb(skinThicknessMm(args.chordMm ?? SKIN_CHORD_OPAQUE_MM));
  const back = sat(SKIN_BACK_WRAP - args.nlBlur) * SKIN_BACK_NORM;
  const out: [number, number, number] = [0, 0, 0];
  for (let c = 0; c < 3; c++) {
    const D = sat(dl[c] + nl[c]);
    const front = sat(args.nlShade) + (D - sat(args.nlShade)) * args.mask;
    const a = args.mask * args.mask * T[c] * (args.transmission ?? 1); // the mask squared (a half-skin hairline texel)
    out[c] = front * (1 - a) + a * back;
  }
  return out;
}

/**
 * THE WHITE FURNACE for one surface point: the response integrated over every light direction on the sphere, relative
 * to Lambert (1 = energy-neutral). Flat-normal case (N_shade = N_blur = N_g = N_b), μ = N·L.
 */
export function skinFurnaceCpu(curvaturePerMm: number, mask: number, lut: { data: Float32Array; width: number; height: number }, lutV: (c: number) => number, samples = 4000, chordMm = SKIN_CHORD_OPAQUE_MM): [number, number, number] {
  const acc = [0, 0, 0]; let lam = 0;
  for (let k = 0; k < samples; k++) {
    const mu = -1 + (2 * (k + 0.5)) / samples;
    // the LUT is sampled at texel centres: evaluate at the column this μ falls in (the shader's bilinear integrates alike)
    const d = skinDiffuseCpu({ nlShade: mu, nlBlur: mu, nlG: mu, nlB: mu, curvaturePerMm, mask, lut, lutV, chordMm });
    for (let c = 0; c < 3; c++) acc[c] += d[c];
    lam += Math.max(0, mu);
  }
  return [acc[0] / lam, acc[1] / lam, acc[2] / lam];
}

// ── Switches ───────────────────────────────────────────────────────────────────────────────────────────────────────

/** `localStorage 'atlas:skin' = 'off'` — the kill switch (read at most once a second). */
let killRead = -1e9; let killed = false;
export function skinKilled(now = Date.now()): boolean {
  if (now - killRead > 1000) {
    killRead = now;
    try { killed = typeof localStorage !== 'undefined' && localStorage.getItem('atlas:skin') === 'off'; } catch { killed = false; }
  }
  return killed;
}
